import React from 'react';
import { StyleSheet } from 'react-native';
import { View } from './tw-wrappers';

interface SemicircleGaugeProps {
  value: number; // 1, 2, or 3
  size?: number;
  strokeWidth?: number;
  color?: string;
  backgroundColor?: string;
}

/**
 * SemicircleGauge - A 3-segment semicircular progress gauge.
 * Uses View clipping and rotation to render a 180-degree arc divided into 3 segments.
 */
export const SemicircleGauge = ({
  value,
  size = 40,
  strokeWidth = 6,
  color = '#22C55E',
  backgroundColor = '#E5E7EB',
}: SemicircleGaugeProps) => {
  const radius = size / 2;
  const clampedValue = Math.min(3, Math.max(0, value));

  // Total angle to fill in a 180-degree gauge (from Left to Right)
  const totalAngle = clampedValue * 60; // 60, 120, or 180

  /**
   * Robust Half-Slice: Renders a slice in one half of a circle.
   * Uses a full-height mask to ensure the clipping is always consistent.
   */
  const renderHalfSlice = (angle: number, isRight: boolean) => (
    <View
      style={{
        position: 'absolute',
        width: radius,
        height: size,
        left: isRight ? radius : 0,
        overflow: 'hidden',
      }}
    >
      <View
        style={{
          width: size,
          height: size,
          left: isRight ? -radius : 0,
          transform: [{ rotate: `${angle}deg` }],
          // Rotation center is naturally the center of this full-size View (radius, radius)
        }}
      >
        <View
          style={{
            width: radius,
            height: size,
            left: isRight ? radius : 0,
            backgroundColor: color,
          }}
        />
      </View>
    </View>
  );

  return (
    <View style={{ width: size, height: radius, overflow: 'hidden' }}>
      {/* 1. SECURE MASK: A full circle clipped by the parent rectangle to ensure a perfect semicircle */}
      <View
        style={{
          width: size,
          height: size,
          borderRadius: radius,
          backgroundColor: backgroundColor,
          overflow: 'hidden',
          position: 'absolute',
          top: 0,
          left: 0,
        }}
      >
        {/* 2. ROTATING FILL */}
        {/* We rotate the entire fill coordinate system by -90 deg so that 0 deg starts at the left edge */}
        <View
          style={{
            width: size,
            height: size,
            position: 'absolute',
            top: 0,
            left: 0,
            transform: [{ rotate: '-90deg' }],
          }}
        >
          {/* We use one half-slice (0-180 support) to fill our 180-degree gauge */}
          {renderHalfSlice(Math.min(totalAngle, 180) - 180, true)}
        </View>

        {/* 3. RADIAL DIVIDERS */}
        {/* We use full-height views rotated correctly to ensure they point exactly to the pivot point */}
        <View
          style={{
            width: 1.2,
            height: size,
            backgroundColor: '#EFF6FF',
            position: 'absolute',
            left: radius - 0.6,
            top: 0,
            transform: [{ rotate: '-30deg' }],
            zIndex: 10,
          }}
        />
        <View
          style={{
            width: 1.2,
            height: size,
            backgroundColor: '#EFF6FF',
            position: 'absolute',
            left: radius - 0.6,
            top: 0,
            transform: [{ rotate: '30deg' }],
            zIndex: 10,
          }}
        />

        {/* 4. INNER HOLE */}
        <View
          style={{
            position: 'absolute',
            top: strokeWidth,
            left: strokeWidth,
            right: strokeWidth,
            bottom: strokeWidth,
            borderRadius: radius - strokeWidth,
            backgroundColor: '#EFF6FF',
            zIndex: 20,
          }}
        />
      </View>
    </View>
  );
};

const styles = StyleSheet.create({});
